/**
 * LexDuo · F1 Ejecuciones · Página detalle
 * Edición de campos básicos + cambio de estado + audit log + acceso al
 * expediente vinculado. Las fases F2-F5 ampliarán las pestañas internas.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Gavel, ArrowLeft, Save, Loader2, FileText, ListChecks,
  Banknote, Hammer, ShieldAlert, ClipboardList, Mail, Trash2,
  History, ExternalLink, AlertTriangle,
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import ValidacionPreEjecucion from '../components/ejecuciones/ValidacionPreEjecucion';
import EmailsLog from '../components/ejecuciones/EmailsLog';
import PresentacionDemanda from '../components/ejecuciones/PresentacionDemanda';  // __V71_TAB_DEMANDA__
import { obtenerToken } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ejecuciones`;

const ESTADOS_LABEL = {
  borrador:             { label: 'Borrador',             color: 'bg-gray-200 text-gray-700' },
  validacion_pendiente: { label: 'Validación pendiente', color: 'bg-amber-100 text-amber-800' },
  validacion_bloqueada: { label: 'Bloqueada',            color: 'bg-red-100 text-red-700' },
  apta_demanda:         { label: 'Apta para demanda',    color: 'bg-blue-100 text-blue-800' },
  demanda_presentada:   { label: 'Demanda presentada',   color: 'bg-indigo-100 text-indigo-800' },
  en_tramitacion:       { label: 'En tramitación',       color: 'bg-violet-100 text-violet-800' },
  suspendida:           { label: 'Suspendida',           color: 'bg-orange-100 text-orange-800' },
  satisfecha:           { label: 'Satisfecha',           color: 'bg-emerald-100 text-emerald-800' },
  archivada:            { label: 'Archivada',            color: 'bg-stone-200 text-stone-600' },
};

// Mismas transiciones que el backend (sincronizadas)
const TRANSICIONES = {
  borrador:               ["validacion_pendiente", "archivada"],
  validacion_pendiente:   ["validacion_bloqueada", "apta_demanda", "archivada"],
  validacion_bloqueada:   ["validacion_pendiente", "apta_demanda", "archivada"],
  apta_demanda:           ["demanda_presentada", "validacion_pendiente", "archivada"],
  demanda_presentada:     ["en_tramitacion", "archivada"],
  en_tramitacion:         ["suspendida", "satisfecha", "archivada"],
  suspendida:             ["en_tramitacion", "archivada"],
  satisfecha:             ["archivada"],
  archivada:              [],
};

const headers = () => {
  const tok = obtenerToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};

const EjecucionDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eje, setEje] = useState(null);
  const [draft, setDraft] = useState(null);
  const [auditItems, setAuditItems] = useState([]);

  // Diálogo cambio de estado
  const [cambioEstado, setCambioEstado] = useState('');
  const [motivoCambio, setMotivoCambio] = useState('');
  const [cambiando, setCambiando] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const [r, ra] = await Promise.all([
        axios.get(`${API}/${id}`, { headers: headers() }),
        axios.get(`${API}/${id}/audit-log`, { headers: headers() }),
      ]);
      setEje(r.data);
      setDraft(r.data);
      setAuditItems(ra.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      navigate('/ejecuciones');
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [id]);

  const guardar = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const body = {
        objeto_descripcion: draft.objeto_descripcion ?? null,
        importe_principal: parseFloat(draft.importe_principal) || 0,
        importe_intereses: parseFloat(draft.importe_intereses) || 0,
        importe_costas: parseFloat(draft.importe_costas) || 0,
        importe_pagado: parseFloat(draft.importe_pagado) || 0,
        importe_consignado: parseFloat(draft.importe_consignado) || 0,
        fecha_resolucion_ejecutar: draft.fecha_resolucion_ejecutar || null,
        fecha_firmeza: draft.fecha_firmeza || null,
        notas: draft.notas ?? null,
        tipo: draft.tipo,
        hacer_personalisimo: (draft.tipo === 'hacer' || draft.tipo === 'mixta') ? draft.hacer_personalisimo : null,
      };
      await axios.put(`${API}/${id}`, body, { headers: headers() });
      toast({ title: 'Guardado', description: 'Cambios aplicados' });
      await cargar();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const aplicarCambioEstado = async () => {
    if (!cambioEstado) return;
    setCambiando(true);
    try {
      await axios.post(`${API}/${id}/estado`,
        { nuevo_estado: cambioEstado, motivo: motivoCambio },
        { headers: headers() });
      toast({ title: 'Estado actualizado' });
      setCambioEstado('');
      setMotivoCambio('');
      await cargar();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setCambiando(false); }
  };

  const borrar = async () => {
    if (!window.confirm(`Borrar la ejecución ${eje.numero_registro}? Sólo se pueden borrar las que están en estado 'borrador'.`)) return;
    try {
      await axios.delete(`${API}/${id}`, { headers: headers() });
      toast({ title: 'Eliminada' });
      navigate('/ejecuciones');
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  if (loading || !eje || !draft) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-700" />
      </div>
    );
  }

  const estadoCfg = ESTADOS_LABEL[eje.estado] || { label: eje.estado, color: 'bg-gray-100' };
  const transicionesDisp = TRANSICIONES[eje.estado] || [];
  const importeTotal = (parseFloat(draft.importe_principal) || 0) + (parseFloat(draft.importe_intereses) || 0) + (parseFloat(draft.importe_costas) || 0);

  return (
    <div className="p-6 space-y-5" data-testid="ejecucion-detalle">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          {/* __V68__ Breadcrumb castellano + deeplinks correctos */}
          <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
            <button onClick={() => navigate('/tareas?tab=ejecuciones')} className="hover:text-violet-700 hover:underline flex items-center gap-1" data-testid="btn-back-tareas-detalle">
              <ArrowLeft className="w-3.5 h-3.5" /> Volver a Tareas
            </button>
            <span className="text-gray-300">/</span>
            <button onClick={() => navigate('/tareas?tab=ejecuciones')} className="hover:text-violet-700 hover:underline" data-testid="btn-back-ejecuciones">
              Listado de ejecuciones
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-violet-700 font-medium">Detalle</span>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
            <Gavel className="w-6 h-6 text-violet-700" />
            <span className="font-mono">{eje.numero_registro}</span>
            <Badge className={`${estadoCfg.color} hover:${estadoCfg.color}`}>{estadoCfg.label}</Badge>
            <Badge variant="outline" className="capitalize">
              {eje.tipo === 'dineraria' && <Banknote className="w-3 h-3 mr-1" />}
              {eje.tipo === 'hacer' && <Hammer className="w-3 h-3 mr-1" />}
              {eje.tipo === 'mixta' && <Gavel className="w-3 h-3 mr-1" />}
              {eje.tipo}
            </Badge>
          </h1>
          {eje.expediente && (
            <Link to={`/expedientes/${eje.expediente.id}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1" data-testid="link-expediente-vinculado">
              <ExternalLink className="w-3 h-3" />
              Expediente {eje.expediente.numero_expediente || `#${eje.expediente.id}`} · {eje.expediente.titulo || ''}
            </Link>
          )}
        </div>

        <div className="flex gap-2 flex-wrap">
          {eje.estado === 'borrador' && (
            <Button variant="outline" size="sm" onClick={borrar} className="border-red-300 text-red-700 hover:bg-red-50" data-testid="btn-borrar-ejecucion">
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar
            </Button>
          )}
          <Button onClick={guardar} disabled={saving} className="bg-violet-700 hover:bg-violet-800" data-testid="btn-guardar-cambios">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando…</> : <><Save className="w-4 h-4 mr-2" />Guardar cambios</>}
          </Button>
        </div>
      </div>

      {/* Cambio de estado */}
      {transicionesDisp.length > 0 && (
        <Card className="border-violet-200">
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row md:items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs">Cambiar estado</Label>
                <Select value={cambioEstado} onValueChange={setCambioEstado}>
                  <SelectTrigger data-testid="select-cambio-estado"><SelectValue placeholder="Selecciona nuevo estado…" /></SelectTrigger>
                  <SelectContent>
                    {transicionesDisp.map(s =>
                      <SelectItem key={s} value={s}>{ESTADOS_LABEL[s]?.label || s}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-xs">Motivo (opcional)</Label>
                <Input value={motivoCambio} onChange={(e) => setMotivoCambio(e.target.value)} placeholder="Justificación del cambio" data-testid="input-motivo-cambio" />
              </div>
              <Button onClick={aplicarCambioEstado} disabled={!cambioEstado || cambiando} className="bg-violet-700 hover:bg-violet-800" data-testid="btn-aplicar-cambio-estado">
                {cambiando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs internas */}
      <Tabs defaultValue="datos" className="w-full">
        <TabsList className="bg-violet-50">
          <TabsTrigger value="datos" data-testid="tab-datos"><FileText className="w-4 h-4 mr-1" /> Datos</TabsTrigger>
          <TabsTrigger value="validacion" data-testid="tab-validacion"><ShieldAlert className="w-4 h-4 mr-1" /> Validación pre-ejecución</TabsTrigger>
          <TabsTrigger value="resolucion" disabled data-testid="tab-resolucion"><ClipboardList className="w-4 h-4 mr-1" /> Resolución + IA <span className="ml-1 text-[10px] text-gray-500">(F3)</span></TabsTrigger>
          <TabsTrigger value="medidas" disabled data-testid="tab-medidas"><ListChecks className="w-4 h-4 mr-1" /> Medidas <span className="ml-1 text-[10px] text-gray-500">(F4/F5)</span></TabsTrigger>
          <TabsTrigger value="emails" data-testid="tab-emails"><Mail className="w-4 h-4 mr-1" /> Emails</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit"><History className="w-4 h-4 mr-1" /> Auditoría</TabsTrigger>
                { ["apta_demanda","demanda_presentada","en_tramitacion","suspendida","satisfecha","archivada"].includes(ejecucion?.estado) && (
          <TabsTrigger value="demanda" data-testid="tab-trigger-demanda">Presentar demanda</TabsTrigger>
        )}
</TabsList>

        {/* Datos básicos */}
        <TabsContent value="datos" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Datos generales</CardTitle>
              <CardDescription>Datos básicos de la ejecución. El análisis IA y la importación automática vienen en F3.</CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Tipo</Label>
                <Select value={draft.tipo} onValueChange={(v) => setDraft({ ...draft, tipo: v })}>
                  <SelectTrigger data-testid="select-tipo-edit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dineraria">Dineraria</SelectItem>
                    <SelectItem value="hacer">De hacer</SelectItem>
                    <SelectItem value="mixta">Mixta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(draft.tipo === 'hacer' || draft.tipo === 'mixta') && (
                <div>
                  <Label className="text-xs">Personalísima</Label>
                  <Select value={draft.hacer_personalisimo === null ? '' : String(draft.hacer_personalisimo)} onValueChange={(v) => setDraft({ ...draft, hacer_personalisimo: v === 'true' })}>
                    <SelectTrigger data-testid="select-personalisimo-edit"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="false">No</SelectItem>
                      <SelectItem value="true">Sí</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">Fecha resolución a ejecutar</Label>
                <Input type="date" value={draft.fecha_resolucion_ejecutar || ''} onChange={(e) => setDraft({ ...draft, fecha_resolucion_ejecutar: e.target.value })} data-testid="input-fres-edit" />
              </div>
              <div>
                <Label className="text-xs">Fecha firmeza</Label>
                <Input type="date" value={draft.fecha_firmeza || ''} onChange={(e) => setDraft({ ...draft, fecha_firmeza: e.target.value })} data-testid="input-ffirm-edit" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Objeto / fallo a cumplir</Label>
                <Textarea rows={4} value={draft.objeto_descripcion || ''} onChange={(e) => setDraft({ ...draft, objeto_descripcion: e.target.value })} data-testid="input-objeto-edit" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Notas internas</Label>
                <Textarea rows={2} value={draft.notas || ''} onChange={(e) => setDraft({ ...draft, notas: e.target.value })} data-testid="input-notas-edit" />
              </div>
            </CardContent>
          </Card>

          {(draft.tipo === 'dineraria' || draft.tipo === 'mixta') && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Importes</CardTitle>
                <CardDescription>Desglose económico. En F3 se cruzará con los pagos detectados del expediente.</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Principal</Label>
                  <Input type="number" step="0.01" value={draft.importe_principal} onChange={(e) => setDraft({ ...draft, importe_principal: e.target.value })} data-testid="input-principal-edit" />
                </div>
                <div>
                  <Label className="text-xs">Intereses</Label>
                  <Input type="number" step="0.01" value={draft.importe_intereses} onChange={(e) => setDraft({ ...draft, importe_intereses: e.target.value })} data-testid="input-intereses-edit" />
                </div>
                <div>
                  <Label className="text-xs">Costas</Label>
                  <Input type="number" step="0.01" value={draft.importe_costas} onChange={(e) => setDraft({ ...draft, importe_costas: e.target.value })} data-testid="input-costas-edit" />
                </div>
                <div>
                  <Label className="text-xs">Importe pagado</Label>
                  <Input type="number" step="0.01" value={draft.importe_pagado} onChange={(e) => setDraft({ ...draft, importe_pagado: e.target.value })} data-testid="input-pagado-edit" />
                </div>
                <div>
                  <Label className="text-xs">Importe consignado</Label>
                  <Input type="number" step="0.01" value={draft.importe_consignado} onChange={(e) => setDraft({ ...draft, importe_consignado: e.target.value })} data-testid="input-consignado-edit" />
                </div>
                <div className="flex items-end">
                  <div className="w-full p-3 bg-violet-50 rounded-md">
                    <div className="text-xs text-gray-500">Total</div>
                    <div className="text-xl font-bold text-violet-800">
                      {importeTotal.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Audit log */}
        <TabsContent value="validacion">
          <ValidacionPreEjecucion ejecucion={eje} onChanged={cargar} />
        </TabsContent>

        <TabsContent value="emails">
          <EmailsLog ejecucionId={eje.id} />
        </TabsContent>

        <TabsContent value="audit">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Auditoría inalterable</CardTitle>
              <CardDescription>Histórico completo de acciones sobre esta ejecución.</CardDescription>
            </CardHeader>
            <CardContent>
              {auditItems.length === 0 ? (
                <div className="text-sm text-gray-500">Sin entradas todavía.</div>
              ) : (
                <div className="space-y-3">
                  {auditItems.map(a => (
                    <div key={a.id} className="border-l-2 border-violet-300 pl-3 py-1" data-testid={`audit-${a.id}`}>
                      <div className="text-xs text-gray-500">
                        {a.created_at ? new Date(a.created_at).toLocaleString('es-ES') : ''}
                        {a.user_email ? ` · ${a.user_email}` : ''}
                      </div>
                      <div className="text-sm font-medium">{a.accion}</div>
                      {a.estado_anterior && a.estado_nuevo && (
                        <div className="text-xs text-gray-600">{a.estado_anterior} → {a.estado_nuevo}</div>
                      )}
                      {a.detalles && Object.keys(a.detalles).length > 0 && (
                        <pre className="text-[10px] bg-gray-50 p-1 mt-1 rounded overflow-x-auto">{JSON.stringify(a.detalles, null, 2)}</pre>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
            { ["apta_demanda","demanda_presentada","en_tramitacion","suspendida","satisfecha","archivada"].includes(ejecucion?.estado) && (
        <TabsContent value="demanda" className="space-y-4 mt-4" data-testid="tab-content-demanda">
          <PresentacionDemanda ejecucionId={ejecucion?.id} ejecucion={ejecucion} onChanged={() => loadEjecucion?.()} />
        </TabsContent>
      )}
</Tabs>
    </div>
  );
};

export default EjecucionDetalle;
